/**
 * Host/path → tenant brand resolver (data-driven).
 *
 * Lets a vendor reach a pre-branded login *before* auth. The org's real theme
 * isn't known until /v1/platform/auth/me resolves, but the host already tells
 * us who's visiting — so `versatile.sahas.cloud` (or the `/v/versatile` path,
 * handy when everything shares app.sahas.cloud) resolves to the vendor palette
 * from the first paint.
 *
 * The brand list is **data-driven**: `loadVendorBrands()` fetches the public
 * `GET /v1/brands` (vendor orgs' `org_branding.theme`) at startup and
 * caches it to localStorage, so a vendor registered via POST /v1/admin/vendors
 * brands its login with NO code change. The bundled preset below is only a
 * first-paint fallback (instant, before the fetch caches). `resolveBrandFromHostname`
 * reads the cache synchronously (warm from a prior load) ∪ the bundled fallback.
 *
 * Fed into OrgThemeProvider as its initial brand (lib/theme/org-theme-provider.tsx): the org's saved look is laid
 * over it once signed in, and the person's own light or dark on top (lib/theme/org-look effectiveTheme).
 * Returns undefined for the default host (→ DEFAULT_THEME, no change).
 *
 * Cohort note: this is the login-surface counterpart to the in-app CoBrandMark
 * (driven by activeOrg.vendorName once signed in). See
 * docs/versatile/specs/tenancy-vendor-portal.md §4.
 */

import type { TenantTheme } from "@/lib/theme/types";
import { THEME_PRESETS } from "@/lib/theme/presets";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";
const CACHE_KEY = "divineiot:vendor-brands";

interface VendorBrand {
  /** Matched when window.location.hostname equals this (case-insensitive). */
  host: string;
  /** …or when the pathname is this prefix (shared-host fallback, e.g. /v/versatile). */
  pathPrefix: string;
  /** The theme to apply (a Partial<TenantTheme> blob). */
  theme: Partial<TenantTheme>;
}

/**
 * Bundled fallback — instant first paint for known vendors before the data-
 * driven list is cached. Today: Versatile. New vendors come from the API.
 */
const versatileTheme = THEME_PRESETS.find((p) => p.id === "versatile")?.theme;
const BUNDLED_BRANDS: VendorBrand[] = versatileTheme
  ? [{ host: "versatile.sahas.cloud", pathPrefix: "/v/versatile", theme: versatileTheme }]
  : [];

function readCache(): VendorBrand[] {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as VendorBrand[]) : [];
  } catch {
    return [];
  }
}

// Loaded synchronously from localStorage on import so the first render already
// has any previously-fetched vendor brands available.
let cachedBrands: VendorBrand[] = typeof window !== "undefined" ? readCache() : [];

/**
 * Fetch the public, data-driven vendor brand list and cache it to localStorage.
 * Fire-and-forget at startup; failures are non-fatal (the bundled fallback +
 * any prior cache still apply). A newly-registered vendor's login brands on the
 * next load once this populates the cache.
 */
export async function loadVendorBrands(): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const res = await fetch(`${API_BASE}/v1/brands`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return;
    const body = (await res.json()) as {
      items?: { slug: string; name: string; theme: Partial<TenantTheme> }[];
    };
    const brands: VendorBrand[] = (body.items ?? [])
      .filter((b) => b.slug && b.theme)
      .map((b) => ({
        host: `${b.slug}.sahas.cloud`,
        pathPrefix: `/v/${b.slug}`,
        theme: b.theme,
      }));
    cachedBrands = brands;
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(brands));
  } catch {
    /* offline / pre-deploy — keep the bundled fallback + any prior cache */
  }
}

/**
 * Resolve the pre-auth brand theme from the current host/path, or undefined
 * when nothing matches (the default Sahas Forge brand). Data-driven brands
 * (cached) win over the bundled fallback. Safe to call during render and in
 * SSR/test (guards on `window`).
 */
export function resolveBrandFromHostname(): Partial<TenantTheme> | undefined {
  if (typeof window === "undefined") return undefined;
  const host = window.location.hostname.toLowerCase();
  const path = window.location.pathname.toLowerCase();
  const all = [...cachedBrands, ...BUNDLED_BRANDS];
  const match = all.find(
    (b) =>
      host === b.host ||
      path === b.pathPrefix ||
      path.startsWith(b.pathPrefix + "/"),
  );
  return match?.theme;
}
